import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useEntitlements } from '../../context/EntitlementContext';
import { isPlatformAdmin } from '../../utils/portalAccess';
import { isPlatformConsoleRole } from '../../utils/hostnamePortal';
import axios from 'axios';
import {
  Building2,
  Users,
  FileText,
  Gavel,
  TrendingUp,
  ArrowRight,
  Activity,
  RefreshCw,
  AlertCircle,
} from 'lucide-react';
import { Button } from '../../components/ui/button';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
export const PLATFORM_DASHBOARD_API = `${BACKEND_URL}/api`;

export const PLATFORM_DASHBOARD_REQUEST_TIMEOUT_MS = 10000;
export const PLATFORM_DASHBOARD_LOADING_TIMEOUT_MS = 12000;

const EMPTY_STATS = {
  agencies: { total: 0, active: 0 },
  users: { total: 0, staff: 0, clients: 0 },
  cases: { total: 0, active: 0 },
  disputes: { total: 0, active: 0 },
};

export function logPlatformDashboardRenderState({
  authLoading,
  sectionAccessLoading,
  dashboardLoading,
  userLoaded,
  role,
  hasPlatformAccess,
  error,
}) {
  console.info(
    `[PlatformDashboardRenderState] authLoading=${Boolean(authLoading)} sectionAccessLoading=${Boolean(sectionAccessLoading)} dashboardLoading=${Boolean(dashboardLoading)} userLoaded=${Boolean(userLoaded)} role=${role || ''} hasPlatformAccess=${Boolean(hasPlatformAccess)} error=${error || ''}`
  );
}

export function logPlatformDashboardRequest({ url, status, completed, error }) {
  console.info(
    `[PlatformDashboardRequest] url=${url || ''} status=${status ?? ''} completed=${Boolean(completed)} error=${error || ''}`
  );
}

export function normalizePlatformStats(data) {
  if (!data || typeof data !== 'object') return { ...EMPTY_STATS };
  return {
    agencies: {
      total: data.agencies?.total ?? 0,
      active: data.agencies?.active ?? 0,
    },
    users: {
      total: data.users?.total ?? 0,
      staff: data.users?.staff ?? 0,
      clients: data.users?.clients ?? 0,
    },
    cases: {
      total: data.cases?.total ?? 0,
      active: data.cases?.active ?? 0,
    },
    disputes: {
      total: data.disputes?.total ?? 0,
      active: data.disputes?.active ?? 0,
    },
  };
}

export function hasPlatformDashboardAccess(user) {
  if (!user) return false;
  if (isPlatformAdmin(user)) return true;
  return isPlatformConsoleRole(user.role);
}

async function requestWithLog(url, config) {
  try {
    const response = await axios.get(url, config);
    logPlatformDashboardRequest({
      url,
      status: response.status,
      completed: true,
      error: '',
    });
    return { ok: true, response, status: response.status };
  } catch (err) {
    const status = err.response?.status ?? (err.code === 'ECONNABORTED' ? 408 : null);
    logPlatformDashboardRequest({
      url,
      status,
      completed: true,
      error: err.response?.data?.detail || err.message || 'request_failed',
    });
    return { ok: false, error: err, status };
  }
}

/**
 * Platform Owner Dashboard - Phase 8
 *
 * Overview of platform-wide statistics and quick actions.
 * Must never trap the page on an indefinite spinner.
 */
const PlatformDashboard = () => {
  const { token, user, loading: authLoading, getAuthHeaders } = useAuth();
  const { loading: sectionAccessLoading, error: sectionAccessError } = useEntitlements();

  const [stats, setStats] = useState(null);
  const [recentAgencies, setRecentAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loadTimedOut, setLoadTimedOut] = useState(false);
  const fetchGenerationRef = useRef(0);

  const role = String(user?.role || '').toLowerCase();
  const hasPlatformAccess = hasPlatformDashboardAccess(user);
  const userLoaded = Boolean(user?.user_id || user?.email);

  const fetchData = useCallback(async () => {
    const generation = ++fetchGenerationRef.current;
    setLoading(true);
    setError(null);
    setLoadTimedOut(false);

    try {
      // Platform owner never depends on agency_id / agency context.
      const headers = typeof getAuthHeaders === 'function' ? getAuthHeaders() : {};
      const stored =
        typeof window !== 'undefined' ? localStorage.getItem('fundelite_token') : null;
      const bearer =
        headers?.Authorization
        || (token ? `Bearer ${token}` : null)
        || (stored ? `Bearer ${stored}` : null);

      const authConfig = {
        headers: bearer ? { Authorization: bearer } : { ...(headers || {}) },
        withCredentials: true,
        timeout: PLATFORM_DASHBOARD_REQUEST_TIMEOUT_MS,
        validateStatus: (status) => (status >= 200 && status < 300) || status === 204,
      };

      const statsUrl = `${PLATFORM_DASHBOARD_API}/owner-console/stats`;
      const agenciesUrl = `${PLATFORM_DASHBOARD_API}/owner-console/agencies?limit=5`;

      const [statsResult, agenciesResult] = await Promise.all([
        requestWithLog(statsUrl, authConfig),
        requestWithLog(agenciesUrl, authConfig),
      ]);

      if (generation !== fetchGenerationRef.current) return;

      if (statsResult.ok) {
        if (statsResult.status === 204 || !statsResult.response?.data) {
          setStats({ ...EMPTY_STATS });
        } else {
          setStats(normalizePlatformStats(statsResult.response.data));
        }
        setError(null);
      } else {
        setStats({ ...EMPTY_STATS });
        setError(
          statsResult.error?.response?.data?.detail
          || statsResult.error?.message
          || 'Failed to load platform dashboard stats'
        );
      }

      if (agenciesResult.ok) {
        const items = agenciesResult.status === 204
          ? []
          : (agenciesResult.response?.data?.items || []);
        setRecentAgencies(Array.isArray(items) ? items : []);
      } else {
        // Agencies widget is optional — never trap the page.
        setRecentAgencies([]);
      }
    } catch (err) {
      if (generation !== fetchGenerationRef.current) return;
      console.error('Failed to fetch platform data:', err);
      setStats({ ...EMPTY_STATS });
      setRecentAgencies([]);
      setError(err.response?.data?.detail || err.message || 'Failed to load platform data');
    } finally {
      if (generation === fetchGenerationRef.current) {
        setLoading(false);
      }
    }
  }, [getAuthHeaders, token]);

  useEffect(() => {
    logPlatformDashboardRenderState({
      authLoading,
      sectionAccessLoading,
      dashboardLoading: loading,
      userLoaded,
      role,
      hasPlatformAccess,
      error: error || sectionAccessError || '',
    });
  }, [
    authLoading,
    sectionAccessLoading,
    loading,
    userLoaded,
    role,
    hasPlatformAccess,
    error,
    sectionAccessError,
  ]);

  useEffect(() => {
    if (authLoading) return undefined;

    if (!userLoaded) {
      setLoading(false);
      setError('Not authenticated');
      return undefined;
    }

    // Platform owners render regardless of section-access / agency context.
    fetchData();
    return undefined;
    // Intentionally key off identity + token, not fetchData identity churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, userLoaded, user?.user_id, token]);

  // Hard stop: never leave the page on an indefinite spinner.
  useEffect(() => {
    if (!loading) return undefined;
    const timer = setTimeout(() => {
      setLoadTimedOut(true);
      setLoading(false);
      setStats((prev) => prev || { ...EMPTY_STATS });
      setError((prev) => prev || 'Dashboard is taking longer than expected. Showing a recoverable state.');
      logPlatformDashboardRenderState({
        authLoading,
        sectionAccessLoading,
        dashboardLoading: false,
        userLoaded,
        role,
        hasPlatformAccess,
        error: 'loading_timeout',
      });
    }, PLATFORM_DASHBOARD_LOADING_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [
    loading,
    authLoading,
    sectionAccessLoading,
    userLoaded,
    role,
    hasPlatformAccess,
  ]);

  if (authLoading || (loading && !loadTimedOut && !stats)) {
    return (
      <div
        className="flex items-center justify-center h-64"
        data-testid="platform-dashboard-loading"
      >
        <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  const displayStats = stats || EMPTY_STATS;
  const showErrorBanner = Boolean(error);

  return (
    <div className="space-y-6" data-testid="platform-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-stone-900">Platform Dashboard</h1>
          <p className="text-slate-500 mt-1">System-wide overview and management</p>
        </div>
        <Button
          onClick={fetchData}
          variant="outline"
          size="sm"
          data-testid="platform-dashboard-refresh"
        >
          <RefreshCw size={16} className="mr-2" />
          Refresh
        </Button>
      </div>

      {showErrorBanner ? (
        <div
          className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
          data-testid="platform-dashboard-error"
        >
          <div className="flex items-start gap-2">
            <AlertCircle size={18} className="mt-0.5 flex-shrink-0" />
            <p>{error}</p>
          </div>
          <Button
            onClick={fetchData}
            variant="outline"
            size="sm"
            data-testid="platform-dashboard-retry"
          >
            Retry
          </Button>
        </div>
      ) : null}

      {/* Platform Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center">
              <Building2 className="text-white" size={28} />
            </div>
            <div>
              <p className="text-sm text-slate-500">Total Agencies</p>
              <p className="text-3xl font-bold text-stone-900">{displayStats.agencies.total}</p>
              <p className="text-xs text-emerald-600">{displayStats.agencies.active} active</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
              <Users className="text-white" size={28} />
            </div>
            <div>
              <p className="text-sm text-slate-500">Total Users</p>
              <p className="text-3xl font-bold text-stone-900">{displayStats.users.total}</p>
              <p className="text-xs text-slate-500">
                {displayStats.users.staff} staff / {displayStats.users.clients} clients
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
              <FileText className="text-white" size={28} />
            </div>
            <div>
              <p className="text-sm text-slate-500">Total Cases</p>
              <p className="text-3xl font-bold text-stone-900">{displayStats.cases.total}</p>
              <p className="text-xs text-emerald-600">{displayStats.cases.active} active</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
              <Gavel className="text-white" size={28} />
            </div>
            <div>
              <p className="text-sm text-slate-500">Total Disputes</p>
              <p className="text-3xl font-bold text-stone-900">{displayStats.disputes.total}</p>
              <p className="text-xs text-amber-600">{displayStats.disputes.active} active</p>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Agencies */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-stone-900">Recent Agencies</h2>
          <Link
            to="/platform/agencies"
            className="text-amber-600 hover:text-amber-700 text-sm font-medium flex items-center gap-1"
          >
            View All <ArrowRight size={14} />
          </Link>
        </div>
        {recentAgencies.length > 0 ? (
          <div className="space-y-4">
            {recentAgencies.map((agency) => (
              <Link
                key={agency.agency_id}
                to={`/platform/agencies/${agency.agency_id}`}
                className="flex items-center justify-between p-4 rounded-lg border border-slate-200 hover:border-amber-300 hover:bg-amber-50/50 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                    <Building2 size={20} className="text-amber-600" />
                  </div>
                  <div>
                    <p className="font-medium text-stone-900">{agency.name || agency.agency_id}</p>
                    <p className="text-sm text-slate-500">
                      {agency.user_count || 0} users · {agency.case_count || 0} active cases
                    </p>
                  </div>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  agency.status === 'active'
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-slate-100 text-slate-600'
                }`}
                >
                  {agency.status || 'active'}
                </span>
              </Link>
            ))}
          </div>
        ) : (
          <div
            className="text-center py-8 text-slate-500"
            data-testid="platform-dashboard-empty-agencies"
          >
            <Building2 size={48} className="mx-auto text-slate-300 mb-4" />
            <p>No agencies found</p>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Link
          to="/platform/agencies"
          className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-shadow flex items-center gap-4"
        >
          <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
            <Building2 className="text-amber-600" size={24} />
          </div>
          <div>
            <p className="font-medium text-stone-900">Manage Agencies</p>
            <p className="text-sm text-slate-500">View and support agencies</p>
          </div>
        </Link>
        <Link
          to="/platform/activity"
          className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-shadow flex items-center gap-4"
        >
          <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
            <Activity className="text-emerald-600" size={24} />
          </div>
          <div>
            <p className="font-medium text-stone-900">Support Activity</p>
            <p className="text-sm text-slate-500">View audit history</p>
          </div>
        </Link>
        <Link
          to="/platform/settings"
          className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-shadow flex items-center gap-4"
        >
          <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
            <TrendingUp className="text-slate-600" size={24} />
          </div>
          <div>
            <p className="font-medium text-stone-900">Platform Settings</p>
            <p className="text-sm text-slate-500">Configure platform</p>
          </div>
        </Link>
      </div>
    </div>
  );
};

export default PlatformDashboard;
